import React, { memo, useEffect, useState, useCallback, useRef } from "react";
import styles from "./index.modules.less";
import classnames from "classnames";
import { List, Steps, Divider, Col, Row, Button, Table, Space, Image } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import ComplaintCard from "./ComplaintCard";
import TextArea from "antd/es/input/TextArea";
import ComplaintConfirmModal from "../../components/ComplaintConfirmModal";
import ConfirmHandle from "../complaint/ConfirmHandle";
import Upload from "../../components/ProUpload";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  adaptComplaintRecord,
  buildRefundPayload,
  canRefundOrder,
  getComplaintDetailViewState,
  getOrderRefundActionState
} from "../newComplaint/adapter";
import {
  getComplaintDetailV2,
  refundV2,
  dealingComplaintCompat,
  replyV2,
  saveComplaintMemoCompat
} from "../../service/complaint";
import { message } from "../../components/Message";
import { isMobileDevice } from "../../utils/util";

const COMPLETE_STATUS = 3;
const REFUNDED_ORDER_STATUS = 70;

const STEPS_ITEMS = [
  { title: "待处理" },
  { title: "处理中" },
  { title: "处理完成" }
];

const STATUS_TEXT_MAP = {
  1: "待处理",
  2: "处理中",
  3: "处理完成"
};

const getRefundMatchKey = (order = {}) => {
  return (
    order.orderId ||
    order.mfr_order_id ||
    order.bankTradeCode ||
    order.bank_trade_code ||
    ""
  );
};

const markOrderRefunded = (order = {}) => {
  return {
    ...order,
    fsmOrderStatus: REFUNDED_ORDER_STATUS,
    orderRefundTime: order.orderRefundTime || order.raw?.order_refund_time || "",
    refundStatus: "refunded",
    refundStatusText: "已退款",
    refundable: false,
    raw: {
      ...(order.raw || {}),
      fsm_order_status: REFUNDED_ORDER_STATUS,
      refund_status: "refunded",
      order_refund_time: order.orderRefundTime || order.raw?.order_refund_time || ""
    }
  };
};

const applyRefundResultToRecord = (baseRecord, refundedOrders, shouldComplete) => {
  if (!baseRecord) return baseRecord;

  const refundedOrderKeys = new Set(
    refundedOrders.map(getRefundMatchKey).filter(Boolean)
  );
  const orderList = (baseRecord.orderList || []).map(order => {
    const key = getRefundMatchKey(order);
    return refundedOrderKeys.has(key) ? markOrderRefunded(order) : order;
  });

  const nextRecord = {
    ...baseRecord,
    orderList,
    firstOrder: orderList[0] || null
  };

  if (shouldComplete) {
    nextRecord.hasNewMsg = 0;
    nextRecord.currStatus = COMPLETE_STATUS;
    nextRecord.currStatusText = STATUS_TEXT_MAP[COMPLETE_STATUS];
    nextRecord.listStatus = COMPLETE_STATUS;
    nextRecord.listStatusText = STATUS_TEXT_MAP[COMPLETE_STATUS];
    nextRecord.raw = {
      ...(baseRecord.raw || {}),
      curr_status: COMPLETE_STATUS,
      has_new_msg: 0
    };
  }

  return nextRecord;
};

const getUploadedImageIds = (images = []) => {
  return images
    .map(item => item?.response || item?.image_id || item?.imageId || item?.url || "")
    .filter(Boolean)
    .join(",");
};

const NewComplaintDetail = () => {
  const { id: complainNo } = useParams();
  const nav = useNavigate();
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => window.innerWidth <= 768);
  const isMobile = isMobileDevice() || isNarrowViewport;

  useEffect(() => {
    const syncViewport = () => setIsNarrowViewport(window.innerWidth <= 768);
    syncViewport();
    window.addEventListener("resize", syncViewport);
    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [replyImages, setReplyImages] = useState([]);
  const [replyUploadKey, setReplyUploadKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [refundModalMode, setRefundModalMode] = useState(null);
  const [selectedRefundOrder, setSelectedRefundOrder] = useState(null);
  const replyImageFormRef = useRef({
    setFieldsValue: values => {
      const images = values?.images;
      setReplyImages(Array.isArray(images) ? images : []);
    }
  });

  const loadDetail = useCallback(async () => {
    if (!complainNo) return;
    setLoading(true);
    try {
      const response = await getComplaintDetailV2({ complain_no: complainNo });
      const adapted = adaptComplaintRecord(response || {});
      setRecord(adapted);
      return adapted;
    } catch (_err) {
      setRecord(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [complainNo]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const goBack = () => {
    nav(-1);
  };

  const handleAllRefundButtonClick = () => {
    if (!record) return;
    const viewState = getComplaintDetailViewState(record);
    if (viewState.shouldConfirmAllRefund) {
      setRefundModalMode("all");
    } else {
      handleAllRefundConfirm();
    }
  };

  const closeRefundModal = () => {
    setRefundModalMode(null);
    setSelectedRefundOrder(null);
  };

  const handleAllRefundConfirm = async () => {
    closeRefundModal();
    if (!record) return;
    const viewState = getComplaintDetailViewState(record);
    const refundableOrders = record.orderList.filter(canRefundOrder);
    const payload = buildRefundPayload(refundableOrders);
    if (!payload) return;

    let shouldRefreshDetail = false;
    setSubmitting(true);
    try {
      await refundV2(payload);
      shouldRefreshDetail = true;
      setRecord(current =>
        applyRefundResultToRecord(
          current || record,
          refundableOrders,
          viewState.shouldAutoCompleteAfterAllRefund
        )
      );
      if (viewState.shouldAutoCompleteAfterAllRefund) {
        try {
          await dealingComplaintCompat({
            complain_no: record.complainNo,
            content: viewState.autoCompleteRemark || "用户客诉已处理完成",
            code: "05"
          });
        } catch (_err) {
          // 退款成功后后端可能已自动关闭客诉，详情仍需刷新到最新状态。
        }
      }
    } catch (_err) {
      // ignore
    } finally {
      if (shouldRefreshDetail) {
        const refreshedRecord = await loadDetail();
        setRecord(current =>
          applyRefundResultToRecord(
            refreshedRecord || current || record,
            refundableOrders,
            viewState.shouldAutoCompleteAfterAllRefund
          )
        );
      }
      setSubmitting(false);
    }
  };

  const handleSingleRefundClick = (order) => {
    setSelectedRefundOrder(order);
    setRefundModalMode("single");
  };

  const handleSingleRefundConfirm = async () => {
    const order = selectedRefundOrder;
    closeRefundModal();
    if (!record) return;
    if (!order) return;
    const payload = buildRefundPayload([order]);
    if (!payload) return;

    setSubmitting(true);
    try {
      await refundV2(payload);
      setRecord(current =>
        applyRefundResultToRecord(current || record, [order], false)
      );
      const refreshedRecord = await loadDetail();
      setRecord(current =>
        applyRefundResultToRecord(refreshedRecord || current || record, [order], false)
      );
    } catch (_err) {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    if (!record) return;
    setSubmitting(true);
    try {
      await dealingComplaintCompat({
        complain_no: record.complainNo,
        content: "用户客诉已处理完成",
        code: "05"
      });
      await loadDetail();
    } catch (_err) {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplySubmit = async () => {
    if (!record || !replyContent.trim()) return;
    const viewState = getComplaintDetailViewState(record);
    const payload = {
      complain_no: record.complainNo,
      content: replyContent,
      source: 1
    };
    const imageIds = getUploadedImageIds(replyImages);
    if (imageIds && !viewState.isAlipay) {
      payload.images = imageIds;
    }
    setSubmitting(true);
    try {
      if (viewState.isAlipay) {
        await saveComplaintMemoCompat(payload);
      } else {
        await replyV2(payload);
      }
      setReplyContent("");
      setReplyImages([]);
      setReplyUploadKey(key => key + 1);
      await loadDetail();
    } catch (_err) {
      if (!!_err.errmsg) {
        message.show({
          type: "error",
          content: _err.errmsg,
        });
      }
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  if (!record) {
    return (
      <div style={{ padding: 20 }}>
        {loading ? "加载中..." : "暂无数据"}
      </div>
    );
  }

  const viewState = getComplaintDetailViewState(record);
  const currStatusText = record.listStatusText || STATUS_TEXT_MAP[record.currStatus] || "未知";
  const refundModalVisible = refundModalMode !== null;
  const isSingleRefundModal = refundModalMode === "single";

  const orderColumns = [
    {
      title: "流水号",
      key: "bankTradeCode",
      dataIndex: "bankTradeCode"
    },
    {
      title: "订单号",
      key: "orderId",
      dataIndex: "orderId"
    },
    {
      title: "商品名称",
      key: "productName",
      dataIndex: "productName"
    },
    {
      title: "金额",
      key: "amount",
      render: (_, order) => <span>￥{(order.amount || 0).toFixed(2)}</span>
    },
    {
      title: "交易时间",
      key: "payTime",
      dataIndex: "payTime"
    },
    {
      title: "退款状态",
      key: "refundStatus",
      render: (_, order) => <span>{order.refundStatusText}</span>
    },
    {
      title: "操作",
      key: "action",
      render: (_, order) => {
        const actionState = getOrderRefundActionState(order);
        return (
          <Space size="middle">
            <Button
              type="primary"
              size="small"
              disabled={actionState.disabled || submitting}
              onClick={() => handleSingleRefundClick(order)}
            >
              {actionState.text}
            </Button>
          </Space>
        );
      }
    }
  ];

  const infoSpan = isMobile ? 12 : 4;

  return (
    <div className={classnames(styles.detailPage, { [styles.isMobile]: isMobile })}>
      <div className={styles.complaintTitle}>
        <div className={styles.goBack} onClick={goBack}>
          <ArrowLeftOutlined className={styles.goBackText} />
          <div className={`${styles.goBackText} ${styles.goBackTextMargin}`}>
            返回列表
          </div>
        </div>
        <Divider type="vertical" />
        <div className={styles.complaintMainTitle}>投诉详情处理</div>
        <div className={styles.complaintTitleTags}>{currStatusText}</div>
      </div>

      <div className={`${styles.compaintInfo} ${styles.borderTopLine}`}>
        <Row gutter={isMobile ? [12, 12] : 32}>
          <Col span={infoSpan}>
            <div className={styles.compaintInfoTitle}>产品名称</div>
            <div
              className={styles.productName}
              title={record.firstOrder ? record.firstOrder.productName : ""}
            >
              {record.firstOrder ? record.firstOrder.productName : "--"}
            </div>
          </Col>
          <Col span={infoSpan}>
            <div className={styles.compaintInfoTitle}>涉及金额</div>
            <h3 className={styles.compaintInfoMoney}>
              ￥{(record.totalAmount || 0).toFixed(2)}
            </h3>
          </Col>
          <Col span={infoSpan}>
            <div className={styles.compaintInfoTitle}>联系方式</div>
            <div className={styles.infoValue}>{record.phoneNo || "--"}</div>
          </Col>
          <Col span={infoSpan}>
            <div className={styles.compaintInfoTitle}>投诉时间</div>
            <div className={styles.infoValue}>{record.complainTime || "--"}</div>
          </Col>
          <Col span={isMobile ? 24 : undefined} className={styles.complainBtnGroup}>
            <Button
              danger
              disabled={viewState.allRefundButtonDisabled || submitting}
              onClick={handleAllRefundButtonClick}
            >
              {viewState.allRefundButtonText}
            </Button>
            {!viewState.shouldUsePhoneCompleteConfirm && (
              <Button
                type="primary"
                className={styles.btnFinsh}
                disabled={!viewState.canHandleComplete || submitting}
                onClick={handleComplete}
              >
                处理完成
              </Button>
            )}
            {viewState.shouldUsePhoneCompleteConfirm && (
              <ConfirmHandle
                complain_no={record.complainNo}
                isCompleteRefund={!viewState.hasRefundableOrders}
                isProcessComplete={!viewState.canHandleComplete}
                processCompleteCB={loadDetail}
              />
            )}
          </Col>
        </Row>
      </div>

      <Steps
        className={styles.compaintInfo}
        current={viewState.currentStep}
        labelPlacement="vertical"
        size={isMobile ? "small" : "default"}
        items={STEPS_ITEMS}
      />

      <div className={styles.complaintDetailWidthWrapper}>
        <List
          header={<h3>关联交易订单</h3>}
          footer={<></>}
          bordered
          className={styles.complaintDetailListStyle}
        >
          {isMobile ? (
            <div className={styles.orderCards}>
              {(record.orderList || []).map(order => {
                const actionState = getOrderRefundActionState(order);
                return (
                  <div key={order.orderId || order.bankTradeCode} className={styles.orderCard}>
                    <div className={styles.orderCardHead}>
                      <div className={styles.orderCardName}>{order.productName || "--"}</div>
                      <div className={styles.compaintInfoMoney}>￥{(order.amount || 0).toFixed(2)}</div>
                    </div>
                    <div className={styles.orderCardRow}>
                      <span>流水号</span>
                      <span>{order.bankTradeCode || "--"}</span>
                    </div>
                    <div className={styles.orderCardRow}>
                      <span>订单号</span>
                      <span>{order.orderId || "--"}</span>
                    </div>
                    <div className={styles.orderCardRow}>
                      <span>交易时间</span>
                      <span>{order.payTime || "--"}</span>
                    </div>
                    <div className={styles.orderCardRow}>
                      <span>退款状态</span>
                      <span>{order.refundStatusText || "--"}</span>
                    </div>
                    <Button
                      block
                      type="primary"
                      disabled={actionState.disabled || submitting}
                      onClick={() => handleSingleRefundClick(order)}
                    >
                      {actionState.text}
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
          <Table
            columns={orderColumns}
            dataSource={record.orderList}
            rowKey="orderId"
            pagination={false}
          />
          )}
        </List>

        <ComplaintCard>
          <div className={styles.ComplaintCardTitle}>
            <h3>处理记录与回复</h3>
          </div>
          <div className={styles.ComplaintCardContent}>
            {viewState.historyList.length > 0 && viewState.historyList.map((item, index) => (
              <div
                key={index}
                className={styles.ComplaintResponse}
                style={{ marginBottom: 12 }}
              >
                <div
                  className={`${styles.ComplaintResponseUser} ${styles.ComplaintUserMarginRight}`}
                >
                  {item.roleText}
                </div>
                <div className={styles.ComplaintResponseSessionWapper}>
                  <div className={styles.ComplaintResponseSession}>
                    {item.isInternal && (
                      <span style={{ color: "#999", fontSize: 12, marginRight: 4 }}>[内部记录]</span>
                    )}
                    {item.content}
                    {Array.isArray(item.images) && item.images.length > 0 && (
                      <div className={styles.replyImages}>
                        {item.images.map((image, imageIndex) => (
                          <Image
                            key={`${image}-${imageIndex}`}
                            alt="图像预览"
                            className={styles.replyImage}
                            width={100}
                            height={100}
                            preview={{ src: image }}
                            src={image}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {item.createdAt && (
                    <div className={styles.ComplaintResponseSessionDate}>
                      {item.createdAt}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {viewState.shouldShowCompletedWechatNotice && (
              <div className={styles.alipayTip}>
                <div className={styles.alipaywrap}>
                  <InfoCircleOutlined className={styles.alipayTipIcon} />
                  <div className={styles.alipayTipText}>
                    该工单已处理完成，微信渠道如有新消息将自动回滚至处理中。
                  </div>
                </div>
              </div>
            )}

            {viewState.isAlipay && (
              <div className={styles.alipayTip}>
                <div className={styles.alipaywrap}>
                  <InfoCircleOutlined className={styles.alipayTipIcon} />
                  <div className={styles.alipayTipText}>
                    支付宝渠道：用户无法收到回复，可在下方记录处理备忘
                  </div>
                </div>
              </div>
            )}

            {viewState.shouldShowPartialRefundNotice && (
              <div className={styles.alipayTip}>
                <div className={styles.alipaywrap}>
                  <InfoCircleOutlined className={styles.alipayTipIcon} />
                  <div className={styles.alipayTipText}>
                    当前客诉已发生部分退款，可继续退款或处理完成。
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.ComplaintCardTitle} style={{ paddingTop: 0 }}>
            <h4>渠道操作区</h4>
          </div>

          {viewState.canReply && (
            <div className={styles.ComplaintCardFooter}>
              <div className={styles.replyInputWrapper}>
                <TextArea
                  className={styles.textAreaStyle}
                  rows={4}
                  placeholder={viewState.replyPlaceholder}
                  value={replyContent}
                  onChange={e => setReplyContent(e.target.value)}
                />
                {!viewState.isAlipay && (
                  <Upload
                    key={replyUploadKey}
                    label="图片上传"
                    form={replyImageFormRef}
                    size={5}
                    name="images"
                    max={4}
                    disabled={submitting}
                    accept=".jpg, .jpeg, .png"
                    extra="请确保上传的和程序内图标一致png格"
                  />
                )}
              </div>
              <Button
                type="primary"
                loading={submitting}
                disabled={!replyContent.trim()}
                onClick={handleReplySubmit}
              >
                {viewState.replyButtonText}
              </Button>
            </div>
          )}
        </ComplaintCard>
      </div>

      <ComplaintConfirmModal
        visible={refundModalVisible}
        title={isSingleRefundModal ? "确认退款？" : "确认全部退款？"}
        description={
          isSingleRefundModal
            ? "此操作将对当前子订单进行退款，且操作不可撤销。"
            : "此操作将对当前客诉单关联的所有未退款订单进行全额退款，且操作不可撤销。"
        }
        confirmText="确认退款"
        loading={submitting}
        onConfirm={isSingleRefundModal ? handleSingleRefundConfirm : handleAllRefundConfirm}
        onCancel={closeRefundModal}
      />
    </div>
  );
};

export default memo(NewComplaintDetail);
